import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Globe,
  FileText,
  Network,
  DollarSign,
  TrendingUp,
  Plus,
  Wand2,
  BarChart3,
  ExternalLink,
  ArrowUpRight,
  ArrowDownRight,
  Users,
  Eye,
  Clock,
} from "lucide-react";
import { sites, clusters, analytics } from "../lib/api";
import type { Site, Cluster, SiteVisitors, CombinedAnalytics } from "../lib/api";
import { usePolling } from "../hooks/use-polling";

interface StatCardProps {
  icon: React.ElementType;
  label: string;
  value: string | number;
  sub?: string;
  color?: string;
  change?: { value: number; label: string };
}

function StatCard({ icon: Icon, label, value, sub, color = "brand", change }: StatCardProps) {
  const colors: Record<string, string> = {
    brand: "bg-brand-500/10 text-brand-400",
    green: "bg-green-500/10 text-green-400",
    purple: "bg-purple-500/10 text-purple-400",
    amber: "bg-amber-500/10 text-amber-400",
  };
  return (
    <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${colors[color]}`}>
          <Icon className="w-4 h-4" />
        </div>
        <span className="text-sm text-gray-400">{label}</span>
      </div>
      <p className="text-2xl font-bold text-gray-100">{value}</p>
      {change ? (
        <div className="flex items-center gap-1 mt-1">
          {change.value > 0 ? (
            <ArrowUpRight className="w-3 h-3 text-green-400" />
          ) : change.value < 0 ? (
            <ArrowDownRight className="w-3 h-3 text-red-400" />
          ) : null}
          <span className={`text-xs ${change.value > 0 ? "text-green-400" : change.value < 0 ? "text-red-400" : "text-gray-500"}`}>
            {change.value > 0 ? "+" : ""}{change.value}% {change.label}
          </span>
        </div>
      ) : sub ? (
        <p className="text-xs text-gray-500 mt-1">{sub}</p>
      ) : null}
    </div>
  );
}

function fmtVisitors(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return String(n);
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [siteList, setSiteList] = useState<Site[]>([]);
  const [clusterList, setClusterList] = useState<Cluster[]>([]);
  const [visitors, setVisitors] = useState<SiteVisitors | null>(null);
  const [combined, setCombined] = useState<CombinedAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadAll = useCallback(() => {
    return Promise.all([
      sites.list(),
      clusters.list(),
      analytics.siteVisitors().catch(() => null),
      analytics.combined(30).catch(() => null),
    ])
      .then(([s, c, v, comb]) => {
        setSiteList(s.data);
        setClusterList(c.data);
        if (v?.data) setVisitors(v.data);
        if (comb?.data) setCombined(comb.data);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { void loadAll(); }, [loadAll]);
  usePolling(loadAll, 15_000);

  const totalDomains = siteList.length;
  const totalPages = siteList.reduce((sum, s) => sum + (s.stats?.pageCount ?? 0), 0);
  const totalRevenue = siteList.reduce((sum, s) => sum + (s.stats?.monthlyRevenue ?? 0), 0);

  const currentMonthVisitors = visitors?.totals.currentMonth ?? 0;
  const lastMonthVisitors = visitors?.totals.lastMonth ?? 0;
  const visitorChange = lastMonthVisitors > 0
    ? Math.round(((currentMonthVisitors - lastMonthVisitors) / lastMonthVisitors) * 100)
    : 0;

  // Build visitor lookup by siteId
  const visitorsBySite = new Map<string, { currentMonth: number; lastMonth: number }>();
  if (visitors?.sites) {
    for (const sv of visitors.sites) {
      visitorsBySite.set(sv.siteId, { currentMonth: sv.currentMonth, lastMonth: sv.lastMonth });
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-6 text-red-400">
        {error}
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-xl font-bold text-gray-100">Overview</h1>
        <p className="text-sm text-gray-500 mt-0.5">Your affiliate network at a glance</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={Globe}
          label="Domains"
          value={totalDomains}
          sub={`${clusterList.length} cluster${clusterList.length !== 1 ? "s" : ""}`}
          color="brand"
        />
        <StatCard
          icon={FileText}
          label="Pages"
          value={totalPages.toLocaleString()}
          sub="across all domains"
          color="purple"
        />
        <StatCard
          icon={TrendingUp}
          label="Visitors This Month"
          value={fmtVisitors(currentMonthVisitors)}
          change={lastMonthVisitors > 0 ? { value: visitorChange, label: "vs last month" } : undefined}
          sub={
            !visitors
              ? "connect Google Analytics in Settings"
              : currentMonthVisitors === 0 && lastMonthVisitors === 0
                ? visitors.sites.some((s) => s.propertyId) ? "no traffic yet" : "link GA properties in Analytics tab"
                : `last month: ${fmtVisitors(lastMonthVisitors)}`
          }
          color="green"
        />
        <StatCard
          icon={DollarSign}
          label="Monthly Revenue"
          value={`$${totalRevenue.toFixed(2)}`}
          sub="est. this month"
          color="amber"
        />
      </div>

      {/* Combined Analytics */}
      {combined && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-medium text-gray-400">Traffic Overview (Last 30 Days)</h2>
            <span className="text-xs text-gray-600">{combined.sitesIncluded} site{combined.sitesIncluded !== 1 ? "s" : ""} tracked</span>
          </div>

          {/* Overview stat row */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <div className="bg-gray-900 border border-gray-800 rounded-lg px-4 py-3">
              <div className="flex items-center gap-2 mb-1">
                <Users className="w-3.5 h-3.5 text-blue-400" />
                <span className="text-xs text-gray-500">Users</span>
              </div>
              <p className="text-lg font-semibold text-gray-100">{combined.overview.totalUsers.toLocaleString()}</p>
            </div>
            <div className="bg-gray-900 border border-gray-800 rounded-lg px-4 py-3">
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp className="w-3.5 h-3.5 text-green-400" />
                <span className="text-xs text-gray-500">Sessions</span>
              </div>
              <p className="text-lg font-semibold text-gray-100">{combined.overview.totalSessions.toLocaleString()}</p>
            </div>
            <div className="bg-gray-900 border border-gray-800 rounded-lg px-4 py-3">
              <div className="flex items-center gap-2 mb-1">
                <Eye className="w-3.5 h-3.5 text-purple-400" />
                <span className="text-xs text-gray-500">Pageviews</span>
              </div>
              <p className="text-lg font-semibold text-gray-100">{combined.overview.totalPageviews.toLocaleString()}</p>
            </div>
            <div className="bg-gray-900 border border-gray-800 rounded-lg px-4 py-3">
              <div className="flex items-center gap-2 mb-1">
                <ArrowDownRight className="w-3.5 h-3.5 text-amber-400" />
                <span className="text-xs text-gray-500">Bounce Rate</span>
              </div>
              <p className="text-lg font-semibold text-gray-100">{(combined.overview.avgBounceRate * 100).toFixed(1)}%</p>
            </div>
            <div className="bg-gray-900 border border-gray-800 rounded-lg px-4 py-3">
              <div className="flex items-center gap-2 mb-1">
                <Clock className="w-3.5 h-3.5 text-cyan-400" />
                <span className="text-xs text-gray-500">Avg Duration</span>
              </div>
              <p className="text-lg font-semibold text-gray-100">{Math.round(combined.overview.avgSessionDuration)}s</p>
            </div>
          </div>

          {/* Daily traffic chart (simple bar chart) */}
          {combined.daily.length > 0 && (
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
              <h3 className="text-xs font-medium text-gray-500 mb-3">Daily Users</h3>
              <div className="flex items-end gap-[2px] h-32">
                {(() => {
                  const maxUsers = Math.max(...combined.daily.map((d) => d.users), 1);
                  return combined.daily.map((d) => {
                    const height = Math.max((d.users / maxUsers) * 100, 2);
                    const dateStr = d.date.length === 8
                      ? `${d.date.slice(4, 6)}/${d.date.slice(6, 8)}`
                      : d.date;
                    return (
                      <div
                        key={d.date}
                        className="flex-1 group relative"
                        title={`${dateStr}: ${d.users} users, ${d.sessions} sessions, ${d.pageviews} pageviews`}
                      >
                        <div
                          className="w-full bg-brand-500/60 hover:bg-brand-400 rounded-t transition-colors"
                          style={{ height: `${height}%` }}
                        />
                      </div>
                    );
                  });
                })()}
              </div>
              <div className="flex justify-between mt-1">
                <span className="text-[10px] text-gray-600">
                  {combined.daily[0]?.date.length === 8
                    ? `${combined.daily[0].date.slice(4, 6)}/${combined.daily[0].date.slice(6, 8)}`
                    : combined.daily[0]?.date}
                </span>
                <span className="text-[10px] text-gray-600">
                  {(() => {
                    const last = combined.daily[combined.daily.length - 1];
                    if (!last) return "";
                    return last.date.length === 8
                      ? `${last.date.slice(4, 6)}/${last.date.slice(6, 8)}`
                      : last.date;
                  })()}
                </span>
              </div>
            </div>
          )}

          {/* Traffic sources + Top pages side by side */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Traffic sources */}
            {combined.trafficSources.length > 0 && (
              <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
                <h3 className="text-xs font-medium text-gray-500 mb-3">Top Traffic Sources</h3>
                <div className="space-y-2">
                  {combined.trafficSources.slice(0, 8).map((ts) => {
                    const maxSessions = combined.trafficSources[0]?.sessions ?? 1;
                    const width = Math.max((ts.sessions / maxSessions) * 100, 4);
                    return (
                      <div key={ts.source} className="flex items-center gap-3">
                        <span className="text-xs text-gray-300 w-24 truncate flex-shrink-0">{ts.source}</span>
                        <div className="flex-1 h-4 bg-gray-800 rounded overflow-hidden">
                          <div
                            className="h-full bg-brand-500/40 rounded"
                            style={{ width: `${width}%` }}
                          />
                        </div>
                        <span className="text-xs text-gray-500 w-12 text-right flex-shrink-0">{ts.sessions}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Top pages across all sites */}
            {combined.topPages.length > 0 && (
              <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
                <h3 className="text-xs font-medium text-gray-500 mb-3">Top Pages (All Sites)</h3>
                <div className="space-y-1.5">
                  {combined.topPages.slice(0, 8).map((pg, i) => (
                    <div key={`${pg.domain}${pg.path}-${i}`} className="flex items-center gap-2 text-xs">
                      <span className="text-gray-600 w-5 text-right flex-shrink-0">{i + 1}.</span>
                      <span className="text-gray-400 truncate flex-1" title={`${pg.domain}${pg.path}`}>
                        <span className="text-gray-600">{pg.domain}</span>
                        {pg.path}
                      </span>
                      <span className="text-gray-500 flex-shrink-0">{pg.pageviews.toLocaleString()} views</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Quick actions */}
      <div>
        <h2 className="text-sm font-medium text-gray-400 mb-3">Quick Actions</h2>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => navigate("/domains")}
            className="flex items-center gap-2 px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Domain
          </button>
          <button
            onClick={() => navigate("/studio")}
            className="flex items-center gap-2 px-4 py-2 bg-gray-800 hover:bg-gray-700 text-gray-100 text-sm font-medium rounded-lg border border-gray-700 transition-colors"
          >
            <Wand2 className="w-4 h-4" />
            Generate Content
          </button>
          <button
            onClick={() => navigate("/content")}
            className="flex items-center gap-2 px-4 py-2 bg-gray-800 hover:bg-gray-700 text-gray-100 text-sm font-medium rounded-lg border border-gray-700 transition-colors"
          >
            <BarChart3 className="w-4 h-4" />
            View Content
          </button>
        </div>
      </div>

      {/* Clusters */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-medium text-gray-400">Domain Clusters</h2>
          <button
            onClick={() => navigate("/websites")}
            className="flex items-center gap-1 text-xs text-brand-400 hover:text-brand-300 transition-colors"
          >
            View all <ExternalLink className="w-3 h-3" />
          </button>
        </div>

        {clusterList.length === 0 ? (
          <div className="bg-gray-900 border border-gray-800 border-dashed rounded-xl p-8 text-center">
            <Network className="w-8 h-8 text-gray-600 mx-auto mb-3" />
            <p className="text-sm text-gray-500">No clusters yet</p>
            <button
              onClick={() => navigate("/websites")}
              className="mt-3 text-xs text-brand-400 hover:text-brand-300 transition-colors"
            >
              Create your first cluster
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {clusterList.map((cluster) => {
              const clusterSites = siteList.filter((s) => s.clusterId === cluster._id);
              const clusterPages = clusterSites.reduce(
                (sum, s) => sum + (s.stats?.pageCount ?? 0),
                0
              );
              return (
                <div
                  key={cluster._id}
                  className="bg-gray-900 border border-gray-800 rounded-xl p-5 hover:border-gray-700 transition-colors cursor-pointer"
                  onClick={() => navigate("/websites")}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="text-sm font-medium text-gray-100">{cluster.name}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{cluster.hubDomain}</p>
                    </div>
                    <span className="text-xs px-2 py-0.5 bg-brand-500/10 text-brand-400 rounded-full capitalize">
                      {cluster.interlinkStrategy.replace("-", " ")}
                    </span>
                  </div>
                  <div className="flex gap-4 text-xs text-gray-500">
                    <span>
                      <span className="text-gray-300 font-medium">{cluster.spokeDomains.length + 1}</span> domains
                    </span>
                    <span>
                      <span className="text-gray-300 font-medium">{clusterPages.toLocaleString()}</span> pages
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Top Domains with GA visitors */}
      {siteList.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-medium text-gray-400">Top Domains</h2>
            <button
              onClick={() => navigate("/domains")}
              className="flex items-center gap-1 text-xs text-brand-400 hover:text-brand-300 transition-colors"
            >
              View all <ExternalLink className="w-3 h-3" />
            </button>
          </div>
          <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-800">
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Domain</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-gray-500">Pages</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-gray-500">This Month</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-gray-500">Last Month</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-gray-500">Revenue</th>
                </tr>
              </thead>
              <tbody>
                {[...siteList]
                  .sort((a, b) => {
                    const av = visitorsBySite.get(a._id)?.currentMonth ?? 0;
                    const bv = visitorsBySite.get(b._id)?.currentMonth ?? 0;
                    return bv - av;
                  })
                  .slice(0, 8)
                  .map((site, i) => {
                    const sv = visitorsBySite.get(site._id);
                    return (
                      <tr
                        key={site._id}
                        className={`hover:bg-gray-800/50 cursor-pointer transition-colors ${
                          i < Math.min(siteList.length, 8) - 1 ? "border-b border-gray-800/50" : ""
                        }`}
                        onClick={() => navigate(`/websites/${site._id}`)}
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div
                              className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                                site.status === "active" ? "bg-green-400" : "bg-gray-600"
                              }`}
                            />
                            <span className="text-gray-200">{site.domain}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right text-gray-400">
                          {site.stats?.pageCount?.toLocaleString() ?? 0}
                        </td>
                        <td className="px-4 py-3 text-right text-gray-400">
                          {sv ? sv.currentMonth.toLocaleString() : <span className="text-gray-600">-</span>}
                        </td>
                        <td className="px-4 py-3 text-right text-gray-400">
                          {sv ? sv.lastMonth.toLocaleString() : <span className="text-gray-600">-</span>}
                        </td>
                        <td className="px-4 py-3 text-right text-gray-400">
                          ${(site.stats?.monthlyRevenue ?? 0).toFixed(2)}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
          {visitors && visitors.sites.filter((s) => !s.propertyId).length > 0 && (
            <p className="text-xs text-gray-600 mt-2">
              {visitors.sites.filter((s) => !s.propertyId).length} site(s) have no GA property linked. Assign properties in each site's Analytics tab.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
